<template>
    <framework>
        <template #side>
            <side-panel-nav/>
        </template>
        <template #main>
            <main-header title="Your Account" icon="fa-user-circle"/>

            <div class="columns mt-6 mb-6">
                <div class="column is-3">
                    <ul class="sub-nav-menu">
                        <li :class="{'active': section === 'account'}" @click="section = 'account'">
                            <i class="fad fa-fw fa-user-gear mr-3"></i>Account Settings
                        </li>
                        <li :class="{'active': section === 'login'}" @click="section = 'login'">
                            <i class="fad fa-fw fa-lock-keyhole mr-3"></i>Login Credentials
                        </li>
                        <li :class="{'active': section === 'billing'}" @click="section = 'billing'">
                            <i class="fad fa-fw fa-credit-card mr-3"></i>Billing Information
                        </li>
                        <li :class="{'active': section === 'schedule'}" @click="section = 'schedule'">
                            <i class="fad fa-fw fa-briefcase mr-3"></i>Work Schedule
                        </li>
                        <li :class="{'active': section === 'certs'}" @click="section = 'certs'">
                            <i class="fas fa-fw fa-certificate mr-3"></i>Certifications & Organizations
                        </li>
                    </ul>
                </div>
                <div class="column is-8 pl-6">
                    <section v-if="section === 'account'">
                        <h2 class="mb-5"><i class="fad fa-user-gear fa-fw mr-3"></i>Account Settings</h2>

                        <ui-card class="content">
                            <p><i class="fas fa-fw fa-user mr-3"></i>Personal Information</p>

                            <div class="columns">
                                <div class="column-is-narrow"><i class="far fa-fw"></i></div>
                                <div class="column is-5">
                                    <div class="has-text-weight-bold is-size-7 ml-3">Full Name</div>
                                    <div class="mt-1 ml-3">
                                        {{ user.first_name }} {{ user.last_name }}
                                        <b-tooltip multilined label="For record keeping purposes, please contact us at support@my247ed.com if you need a name change. Please include a photo of your updated identification with your request.">
                                            <i class="far fa-info-circle ml-2"></i>
                                        </b-tooltip>
                                    </div>
                                </div>
                                <div class="column">
                                    <b-field label="Maiden Name" label-position="inside">
                                        <b-input v-model="user.maiden_name"></b-input>
                                    </b-field>
                                </div>
                                <div class="column">
                                    <b-field label-position="inside" label="Date of Birth">
                                        <b-input v-model="user.dob_on" v-mask="'##/##/####'" placeholder="mm/dd/yyyy"></b-input>
                                    </b-field>
                                </div>
                            </div>
                            <div class="columns">
                                <div class="column-is-narrow"><i class="far fa-fw"></i></div>
                                <div class="column is-3">
                                    <b-field label="Title" label-position="inside">
                                        <b-select v-model="user.title" expanded>
                                            <option v-for="option in titleOptions" :value="option">{{ option }}</option>
                                        </b-select>
                                    </b-field>
                                </div>
                                <div class="column">
                                    <gender-field v-model="user.gender"/>
                                </div>
                            </div>
                            <p><i class="fas fa-fw fa-address-card mr-3"></i>Contact Information</p>
                            <div class="columns">
                                <div class="column-is-narrow"><i class="far fa-fw"></i></div>
                                <div class="column is-7">
                                    <b-field label="Address" label-position="inside" message="Number, street, etc.">
                                        <b-input v-model="user.address"></b-input>
                                    </b-field>
                                </div>
                                <div class="column">
                                    <b-field label="Address 2" label-position="inside" message="Appt. Number, Suite, etc.">
                                        <b-input v-model="user.address_2"></b-input>
                                    </b-field>
                                </div>
                            </div>
                            <div class="columns">
                                <div class="column-is-narrow"><i class="far fa-fw"></i></div>
                                <div class="column">
                                    <b-field label="City" label-position="inside">
                                        <b-input v-model="user.city"></b-input>
                                    </b-field>
                                </div>
                                <div class="column">
                                    <b-field label="State" label-position="inside">
                                        <b-select v-model="user.state" expanded>
                                            <option value="AL">Alabama</option>
                                            <option value="AK">Alaska</option>
                                            <option value="AZ">Arizona</option>
                                            <option value="AR">Arkansas</option>
                                            <option value="CA">California</option>
                                            <option value="CO">Colorado</option>
                                            <option value="CT">Connecticut</option>
                                            <option value="DE">Delaware</option>
                                            <option value="DC">District Of Columbia</option>
                                            <option value="FL">Florida</option>
                                            <option value="GA">Georgia</option>
                                            <option value="HI">Hawaii</option>
                                            <option value="ID">Idaho</option>
                                            <option value="IL">Illinois</option>
                                            <option value="IN">Indiana</option>
                                            <option value="IA">Iowa</option>
                                            <option value="KS">Kansas</option>
                                            <option value="KY">Kentucky</option>
                                            <option value="LA">Louisiana</option>
                                            <option value="ME">Maine</option>
                                            <option value="MD">Maryland</option>
                                            <option value="MA">Massachusetts</option>
                                            <option value="MI">Michigan</option>
                                            <option value="MN">Minnesota</option>
                                            <option value="MS">Mississippi</option>
                                            <option value="MO">Missouri</option>
                                            <option value="MT">Montana</option>
                                            <option value="NE">Nebraska</option>
                                            <option value="NV">Nevada</option>
                                            <option value="NH">New Hampshire</option>
                                            <option value="NJ">New Jersey</option>
                                            <option value="NM">New Mexico</option>
                                            <option value="NY">New York</option>
                                            <option value="NC">North Carolina</option>
                                            <option value="ND">North Dakota</option>
                                            <option value="OH">Ohio</option>
                                            <option value="OK">Oklahoma</option>
                                            <option value="OR">Oregon</option>
                                            <option value="PA">Pennsylvania</option>
                                            <option value="RI">Rhode Island</option>
                                            <option value="SC">South Carolina</option>
                                            <option value="SD">South Dakota</option>
                                            <option value="TN">Tennessee</option>
                                            <option value="TX">Texas</option>
                                            <option value="UT">Utah</option>
                                            <option value="VT">Vermont</option>
                                            <option value="VA">Virginia</option>
                                            <option value="WA">Washington</option>
                                            <option value="WV">West Virginia</option>
                                            <option value="WI">Wisconsin</option>
                                            <option value="WY">Wyoming</option>
                                        </b-select>
                                    </b-field>
                                </div>
                                <div class="column">
                                    <b-field label="Zip" label-position="inside">
                                        <b-input v-model="user.zip" v-mask="'#####'"></b-input>
                                    </b-field>
                                </div>
                            </div>
                            <hr/>
                            <div class="columns">
                                <div class="column-is-narrow"><i class="far fa-fw"></i></div>
                                <div class="column">
                                    <b-field label="Home (Cell) Phone" label-position="inside">
                                        <b-input v-model="user.phone" v-mask="'(###) ###-####'"></b-input>
                                    </b-field>
                                </div>
                                <div class="column">
                                    <b-field label="Work Phone" label-position="inside">
                                        <b-input v-model="user.phone_2" v-mask="'(###) ###-####'"></b-input>
                                    </b-field>
                                </div>
                            </div>

                            <template #footer>
                                <div class="has-text-right">
                                    <b-button type="is-primary" size="is-small" @click="updateAccount" :disabled="processing || !user.first_name || !user.last_name || !user.dob_on || !user.address || !user.city || !user.state || !user.zip || !user.phone || !user.email"><i class="fas fa-check mr-2"></i>Save Changes</b-button>
                                </div>
                            </template>
                        </ui-card>
                    </section>

                    <section v-if="section === 'billing'">
                        <h2 class="mb-5"><i class="fad fa-credit-card fa-fw mr-3"></i>Billing Information</h2>

                        <ui-card class="content">
                            <p class="mt-3 mb-4">
                                <i class="far fa-info-circle fa-fw mr-2 ml-4"></i>Payment information is not required to be on file for your account.
                            </p>
                        </ui-card>

                        <h2 class="mb-5 mt-6 pt-5"><i class="fad fa-money-check-alt fa-fw mr-3"></i>Payment Plans</h2>

                        <ui-card v-if="paymentPlans.length" v-for="plan in paymentPlans">
                            <div class="columns">
                                <div class="column is-narrow">
                                    <i class="fad fa-money-check-alt has-text-primary fa-2x"></i>
                                </div>
                                <div class="column">
                                    <div class="has-text-weight-bold mb-3 is-size-5">
                                        {{plan.nickname}}
                                    </div>

                                    <div>
                                        <strong>{{plan.agreement_total | currency}}</strong> at <strong>{{plan.monthly | currency}}/mo</strong> for <strong>{{plan.term}}</strong> month(s) starting on <strong>{{plan.defer_on | moment('MMMM Do, YYYY')}}</strong>.
                                    </div>
                                    <p class="has-text-grey mt-4" v-if="!plan.is_tpo">
                                        This is a legacy payment plan. For questions or to make changes to your plan, please contact us at <a href="mailto:support@my247ed.com">support@my247ed.com</a>.
                                    </p>
                                    <div v-else>
                                        <p class="has-text-grey mt-4" >
                                            This payment plan is managed by a private lender, <strong>The Payment Option (TPO)</strong>. Additional information and payment support can be found on their site.  Click 'Manage Payment Plan' to view and access your payment plan.
                                        </p>
                                        <p class="mt-3"><strong>Account: </strong> {{currentUser.email}}</p>
                                    </div>
                                </div>
                            </div>
                            <template #footer>
                                <div class="columns">
                                    <div class="column">

                                    </div>
                                    <div class="column has-text-right" v-if="plan.is_tpo">
                                        <b-button tag="a" type="is-primary" href="https://app.thepaymentoption.com" target="_blank" size="is-small"><i class="fas fa-up-right-from-square mr-2"></i>Manage Payment Plan</b-button>
                                    </div>
                                </div>
                            </template>
                        </ui-card>
                        <p class="has-text-white" v-else>
                            <i class="far fa-info-circle fa-fw mr-4 ml-5 pl-3"></i>You do not have any payment plans.
                        </p>


                    </section>

                    <section v-if="section === 'login'">
                        <h2 class="mb-5"><i class="fad fa-lock-keyhole fa-fw mr-3"></i>Login Credentials</h2>

                        <ui-card class="content">
                            <div class="columns">
                                <div class="column">
                                    <b-field label="Primary E-Mail Address" label-position="inside">
                                        <b-input v-model="user.email"></b-input>
                                    </b-field>
                                </div>
                            </div>
                            <div class="columns">
                                <div class="column">
                                    <b-field label="Secondary E-Mail Address" label-position="inside" message="If you have a second email address enter it as an alternate. It can be used to login and lets us contact you in case your primary e-mail fails.">
                                        <b-input v-model="user.email_alt"></b-input>
                                    </b-field>
                                </div>
                            </div>
                            <div class="columns">
                                <div class="column">
                                    <b-field label="Password" label-position="inside">
                                        <b-input placeholder="Set to change your password..." v-model="user.password"></b-input>
                                    </b-field>
                                </div>
                            </div>

                            <template #footer>
                                <div class="has-text-right">
                                    <b-button type="is-primary" size="is-small" @click="updateAccount" :disabled="processing || !user.first_name || !user.last_name || !user.dob_on || !user.address || !user.city || !user.state || !user.zip || !user.phone || !user.email"><i class="fas fa-check mr-2"></i>Save Changes</b-button>
                                </div>
                            </template>
                        </ui-card>
                    </section>

                    <section v-if="section === 'schedule'">
                        <h2 class="mb-5"><i class="fad fa-briefcase fa-fw mr-3"></i>Work Schedule</h2>
                        <contracted-schedule-card/>
                    </section>

                    <section v-if="section === 'certs'">
                        <h2 class="mb-5"><i class="fas fa-certificate fa-fw mr-3"></i>Organizations & Certifications</h2>

                        <p class="has-text-white mb-6">my247ed.com provides courses, online and in-person, training, clinics, in-person and on-demand, on-boarding to various platforms, and offerings for many different organizations and their certifications types. Below are the certifications and organizations you have selected. Once added to your account you can browse and manage their offerings.</p>

                        <ui-card>
                            <div class="columns">
                                <div class="column is-narrow">
                                    <i class="fad fa-certificate has-text-primary fa-2x"></i>
                                </div>
                                <div class="column">
                                    <div class="has-text-weight-bold mb-3 is-size-5">
                                        Washington State Professional Development
                                    </div>
                                    <p class="has-text-grey">
                                        my247ed.com partners with many Washington State approved clock-hour providers for Washington State Professional Development opportunities. All courses and conferences found on our platform are guaranteed approved or accredited per all state requirements.
                                    </p>

                                    <h4><i class="fad fa-diagram-project mr-2"></i>Integrations</h4>


                                    <div class="mb-3">
                                        <strong>Washington OSPI Certification</strong> (<a href="https://www.k12.wa.us/certification" target="_blank">https://www.k12.wa.us/certification</a>)
                                    </div>
                                    <div class="columns">
                                        <div class="column is-4">
                                            <img src="@/assets/partner_assets/ospi.png">
                                        </div>
                                        <div class="column"></div>
                                        <div class="column is-5">
                                            <b-field label="OSPI Cert Number" label-position="inside">
                                                <b-input v-model="ospi.certification_number" placeholder="ie. 000000F" :disabled="!!ospi.locked_at"></b-input>
                                            </b-field>
                                            <div class="is-size-7 has-text-grey" v-if="ospi.locked_at">
                                                <i class="fas fa-lock mr-2"></i>This ID is locked to your account. <a href="mailto:support@my247ed.com">Contact support</a> if it needs to be changed.
                                            </div>
                                        </div>
                                    </div>

                                    <b-message type="is-warning" v-if="ospi.conflicts">
                                        <i class="fas fa-exclamation-triangle mr-2"></i>The OSPI Cert Number you have entered is in use by another account. If you have multiple accounts using the same ID this may lead to complications in accurately tracking and synchronizing your records and certifications. Please verify your ID is correct or <a href="mailto:support@my247ed.com">contact support</a> for assistance.
                                    </b-message>
                                </div>
                                <div class="column has-text-right is-narrow">

                                </div>
                            </div>
                            <template #footer>
                                <div class="columns">
                                    <div class="column">

                                    </div>
                                    <div class="column has-text-right">
                                        <b-button type="is-primary" size="is-small" :disabled="!!ospi.locked_at" @click="updateCert(ospi)"><i class="fas fa-check mr-2"></i>Save Changes</b-button>
                                    </div>
                                </div>
                            </template>
                        </ui-card>
                        <ui-card class="mt-4">
                            <div class="columns">
                                <div class="column is-narrow">
                                    <i class="fad fa-certificate has-text-primary fa-2x"></i>
                                </div>
                                <div class="column">
                                    <div class="has-text-weight-bold mb-3 is-size-5">
                                        Washington Interscholastic Activities Association (WIAA)
                                    </div>
                                    <p class="has-text-grey">
                                        The Washington Interscholastic Activities Association (WIAA) is a private, nonprofit 501(c)(3) service organization and rule-making body that was formed in 1905 to create equitable playing conditions between high school sports teams in Washington.
                                    </p>
                                    <p class="mt-3"><a href="https://www.wiaa.com" target="_blank">https://www.wiaa.com</a></p>

                                    <h4><i class="fad fa-diagram-project mr-2"></i>Integrations</h4>


                                    <div class="mb-3">
                                        <strong>Final Forms</strong> (<a href="https://www.finalforms.com" target="_blank">https://www.finalforms.com</a>)
                                    </div>
                                    <div class="columns">
                                        <div class="column is-4">
                                            <img src="@/assets/partner_assets/finalforms.jpg">
                                        </div>
                                        <div class="column"></div>
                                        <div class="column is-5">
                                            <b-field label="WIAA Membership ID" label-position="inside">
                                                <b-input v-model="wiaa.certification_number" placeholder="ie. WA-0000-0000" :disabled="!!wiaa.locked_at"></b-input>
                                            </b-field>
                                            <div class="is-size-7"><a href="https://wiaa.finalforms.com/lookup_membership_id" target="_blank"><i class="fas fa-search mr-2"></i>Lookup WIAA Membership ID</a></div>
                                            <div class="is-size-7 has-text-grey" v-if="wiaa.locked_at">
                                                <i class="fas fa-lock mr-2"></i>This ID is locked to your account. <a href="mailto:support@my247ed.com">Contact support</a> if it needs to be changed.
                                            </div>
                                        </div>
                                    </div>

                                    <b-message type="is-warning" v-if="wiaa.conflicts">
                                        <i class="fas fa-exclamation-triangle mr-2"></i>The WIAA Membership ID you have entered is in use by another account. If you have multiple accounts using the same ID this may lead to complications in accurately tracking and synchronizing your records and certifications. Please verify your ID is correct or <a href="mailto:support@my247ed.com">contact support</a> for assistance.
                                    </b-message>
                                </div>
                                <div class="column has-text-right is-narrow">

                                </div>
                            </div>

                            <template #footer>
                                <div class="columns">
                                    <div class="column">

                                    </div>
                                    <div class="column has-text-right">
                                        <b-button type="is-primary" size="is-small" :disabled="!!wiaa.locked_at" @click="updateCert(wiaa)"><i class="fas fa-check mr-2"></i>Save Changes</b-button>

                                    </div>
                                </div>
                            </template>
                        </ui-card>
                        <b-button class="mt-6" @click="addCertification"><i class="far fa-plus-circle mr-2"></i>Add Certification or Organization</b-button>
                    </section>

                </div>
            </div>

        </template>
    </framework>
</template>

<script>
    import MainHeader from "../../components/framework/MainHeader";
    import Framework from "../../components/framework/Framework";
    import SidePanelNav from "../../components/framework/SidePanelNav";
    import {mapGetters, mapMutations} from "vuex";
    import UiCard from "../../components/UiCard";
    import ContractedScheduleCard from "../../components/studyActivity/ContractedScheduleCard";
    import GenderField from "../../components/fields/GenderField";
    import {TITLE_OPTIONS} from "../../constants/profile";
    import {client as http} from "../../http_client";
    import moment from "moment";
    import async from "async";

    export default {
        components: {UiCard, Framework, SidePanelNav, MainHeader, GenderField, ContractedScheduleCard},
        data: function() {
            return {
                section: 'account',
                user: {},
                titleOptions: TITLE_OPTIONS,
                certifications: [],
                ospi: {},
                wiaa: {},
                processing: false,
                paymentPlans: []
            };
        },
        computed: {
            ...mapGetters(['currentUser'])
        },
        methods: {
            load: function() {
                async.parallel([
                    (cb) => {
                        http.get('app/users/certifications', {force: true}).then(response => {
                            this.certifications = response.data;

                            for (let i = 0; i < this.certifications.length; i++) {
                                const certification = this.certifications[i];
                                if (certification.certification_id === 1) {
                                    this.ospi = certification;
                                }
                                if (certification.certification_id === 2) {
                                    this.wiaa = certification;
                                }
                            }

                            // temp fix for missing certifications
                            if (!this.ospi.certification_id) this.ospi = {certification_id: 1, certification_number: null, integration_id: 1};
                            if (!this.wiaa.certification_id) this.wiaa = {certification_id: 2, certification_number: null, integration_id: 2};

                            cb();
                        }).catch(cb);
                    },
                    (cb) => {
                        http.get('app/users/payment-plans').then(response => {
                            this.paymentPlans = response.data;
                            cb();
                        }).catch(cb);
                    }
                ], (err) => {
                    if (err) {
                        console.error(err);
                    }
                });
            },
            updateCert: function(cert) {
                http.put('app/users/certifications/' + cert.certification_id, cert).then(response => {
                    this.$notify('success', 'Settings Saved', 'Your changes have been successfully applied.', {duration: 5000, permanent: false});
                    this.load();
                }).catch(err => {
                    if (err.response && err.response.status === 409) {
                        this.$notify('error', 'ID Locked', 'This ID is locked to your account. Please contact support to change it.', {duration: 8000, permanent: false});
                    } else {
                        this.$notify('error', 'Save Failed', 'Your changes could not be saved. Please try again later.', {duration: 5000, permanent: false});
                    }
                    this.load();
                });
            },
            addCertification: function() {
                window.alert('There are currently no more Certifications or Organizations available to add.');
            },
            updateAccount: function() {
                this.processing = true;
                http.put('app/users/', {
                    maiden_name: this.user.maiden_name,
                    title: this.user.title,
                    gender: this.user.gender,
                    password: this.user.password,
                    dob: this.user.dob_on,
                    email: this.user.email,
                    email_2: this.user.email_alt,
                    address: this.user.address,
                    address_2: this.user.address_2,
                    city: this.user.city,
                    state: this.user.state,
                    zip: this.user.zip,
                    phone: this.user.phone,
                    phone_2: this.user.phone_2,
                    district: this.user.district,
                    school: this.user.school,
                    subject_area: this.user.subject_area,
                    grade_level: this.user.grade_level
                }).then(response => {
                    this.user.password = null;
                    this.$notify('success', 'Settings Saved', 'Your changes have been successfully applied.', {duration: 5000, permanent: false});
                    this.processing = false;
                    // TODO doesn't update the user in store?
                }).catch(err => {
                    this.processing = false;
                    const message = (err.response && err.response.data) ? err.response.data : 'Something went wrong saving your changes. Please try again.';
                    this.$notify('error', 'Could Not Save', message, {duration: 8000, permanent: false});
                });
            }
        },
        mounted: function() {
            this.user = {password: null, ...this.currentUser};
            this.user.dob_on = moment(this.user.dob_on).format('MM/DD/YYYY');
            this.load();
        }
    };
</script>

<style lang="scss" scoped>
    .sub-nav-menu {
        margin: 0;
        padding: 0;
        list-style: none;
        border-radius: 0.5rem;
        box-shadow: 5px 5px 30px 0 rgba(0, 0, 0, 0.2);

        li {
            font-weight: 500;
            padding: 0.7rem 1.2rem;
            border-bottom: 1px solid #f4f4f4;
            color: #525252;
            background: white;
            font-size: 1rem;
            cursor: pointer;

            &.active {
                background-color: #336699;
                color: white;
            }

            &:first-child {
                border-top-left-radius: 0.5rem;
                border-top-right-radius: 0.5rem;
            }

            &:last-child {
                border-bottom-left-radius: 0.5rem;
                border-bottom-right-radius: 0.5rem;
                border: none;
            }
        }
    }

    h2 {
        font-size: 1.2rem;
        color: white;
        padding-bottom: 0.5rem;
        margin-bottom: 1rem;
        border-bottom: 1px dashed rgba(255, 255, 255, 0.2);

        i {
            color: white;
        }
    }

    h4 {
        font-size: 1.2rem;
        margin-top: 1.5rem;
        margin-bottom: 2rem;
        padding-bottom: 0.8rem;

        i {
            color: #336699;
        }

        border-bottom: 1px dashed #ddd;
    }
</style>
